import { presignedUrl } from '@/modules/Upload/services'
import { Attachment } from '@/modules/Upload/types'
import { ErrorResponse } from '@/types/shared'
import { useMutationWithHandler } from './useMutationWithHandler'
import axios, { AxiosError, CancelTokenSource } from 'axios'
import { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface UseUploadThumbnailsProps {
    prefix: string
    onSuccess?: (data: PromiseSettledResult<Partial<Attachment>>[]) => void
    onError?: (err: AxiosError<ErrorResponse>) => void
    isPublic?: boolean
}

const REMOVE_DURATION = 5000 // ms

const useUploadThumbnails = ({
    onSuccess,
    onError,
    prefix,
    isPublic = true,
}: UseUploadThumbnailsProps) => {
    const { t } = useTranslation()
    const [percent, setPercent] = useState<Record<string, number>>({})
    // Store cancel tokens for each file upload
    const cancelTokensRef = useRef<Record<string, CancelTokenSource>>({})
    // Add this useRef to track which files have a timeout set
    const completedTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({})

    const uploadFile = async (
        file: File,
        thumbnailKey?: string,
        isThumbnail?: boolean
    ): Promise<Partial<Attachment>> => {
        const response = await presignedUrl({
            prefix,
            key: file.name,
            isPublic,
        })

        if (!response?.result?.url) {
            throw new Error(t(`upload-file-failed`))
        }

        const metadata = thumbnailKey
            ? {
                  'x-amz-meta-thumbnail': thumbnailKey, // Custom metadata
              }
            : {}

        // Create a new cancel token for this file
        const cancelTokenSource = axios.CancelToken.source()
        cancelTokensRef.current[file.name] = cancelTokenSource

        await axios
            .put(response?.result?.url, file, {
                onUploadProgress: (p) => {
                    const { loaded, total } = p
                    const percent = Math.floor((loaded * 100) / (total || 1))
                    if (!isThumbnail) {
                        setPercent((prev) => ({
                            ...prev,
                            [file.name]: percent,
                        }))
                        // updatePercentUpload(file.name, percent)
                    }
                },
                headers: {
                    'Content-Type': file.type,
                    ...metadata,
                },
                cancelToken: cancelTokenSource.token,
            })
            .catch((err) => {
                // Check if the request was cancelled intentionally
                if (axios.isCancel(err)) {
                    // Clear the percent for this file
                    setPercent((prev) => {
                        const updated = { ...prev }
                        delete updated[file.name]
                        return updated
                    })
                    // removePercentUpload(file.name)
                    throw new Error(`Upload cancelled: ${file.name}`)
                }
                console.log(err)
                throw new Error(t(`upload-file-failed`))
            })
            .finally(() => {
                // Clean up the cancel token reference
                delete cancelTokensRef.current[file.name]
            })

        return {
            attachment_file_url: [
                `${prefix}`,
                `${encodeURIComponent(file.name)}`,
            ]
                .join('/')
                .replace('//', '/'),
            attachment_file_size: file.size,
            attachment_file_name: file.name,
            attachment_file_type: file.type,
        }
    }

    const uploadFiles = async (
        files: File[],
        thumbnailKeys?: (string | undefined)[],
        isThumbnail?: boolean
    ) => {
        return Promise.allSettled(
            files.map(async (file, index) => {
                const thumbnailKey = thumbnailKeys?.[index]
                return await uploadFile(file, thumbnailKey, isThumbnail)
            })
        )
    }

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: ({
                files,
                thumbnailKeys,
                isThumbnail,
            }: {
                files: File[]
                thumbnailKeys?: (string | undefined)[]
                isThumbnail?: boolean
            }) => uploadFiles(files, thumbnailKeys, isThumbnail),
        },
        {
            reactQueryKeys: QUERY_KEYS.DRIVE,
            showSuccessToast: false,
            showErrorToast: false,
            onSuccess: (data) => onSuccess?.(data),
            onError: (err: AxiosError<ErrorResponse>) => onError?.(err),
        }
    )

    const abortUpload = (fileName: string): boolean => {
        const cancelToken = cancelTokensRef.current[fileName]
        if (cancelToken) {
            cancelToken.cancel(`Upload cancelled by user: ${fileName}`)
            return true
        }
        return false
    }

    const abortAllUploads = (): void => {
        Object.entries(cancelTokensRef.current).forEach(
            ([fileName, cancelToken]) => {
                cancelToken.cancel(`Upload cancelled by user: ${fileName}`)
            }
        )
        // Reset all percentages
        setPercent({})
        // setPercentUpload({})
        // Clear all cancel tokens
        cancelTokensRef.current = {}
    }

    // Automatically remove files from percent after they reach 100%
    useEffect(() => {
        Object.entries(percent).forEach(([fileName, value]) => {
            if (value === 100 && !completedTimeoutsRef.current[fileName]) {
                // Set a timeout to remove the file after 5 seconds
                completedTimeoutsRef.current[fileName] = setTimeout(() => {
                    setPercent((prev) => {
                        const updated = { ...prev }
                        delete updated[fileName]
                        return updated
                    })
                    // removePercentUpload(fileName)
                    // Clean up the timeout reference
                    delete completedTimeoutsRef.current[fileName]
                }, REMOVE_DURATION)
            }
        })

        // Clean up timeouts if files are removed before timeout fires
        Object.keys(completedTimeoutsRef.current).forEach((fileName) => {
            if (!(fileName in percent)) {
                clearTimeout(completedTimeoutsRef.current[fileName])
                delete completedTimeoutsRef.current[fileName]
            }
        })
    }, [percent])

    return {
        uploadAsync: mutateAsync,
        isUploading: isPending,
        percent,
        abortUpload,
        abortAllUploads,
    }
}

export default useUploadThumbnails
